import React from 'react';

/**
 * Generic modal shell.
 *
 * Before refactoring, CustomerModal, CategoryModal, SupplierModal,
 * TaxModal, and DeleteConfirmModal each duplicated the same ~40 lines
 * of overlay + content box + button group styling.
 *
 * Now they render their unique form content as `children` inside this
 * shared wrapper.
 *
 * Props:
 *  - isOpen       – show / hide
 *  - onClose      – close handler
 *  - onSubmit     – form submit handler (if provided, wraps children in <form>)
 *  - title        – modal heading
 *  - width        – Tailwind max-width class (default 'max-w-md')
 *  - submitLabel  – text for the submit button (default 'Save')
 *  - cancelLabel  – text for the cancel button (default 'Cancel')
 *  - footer       – optional custom footer JSX (overrides default buttons)
 */
export default function Modal({
  isOpen,
  onClose,
  onSubmit,
  title,
  width = 'max-w-md',
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  footer,
  children,
}) {
  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit?.(e);
  };

  const content = (
    <>
      {children}
      {!footer && (
        <div className="flex justify-end gap-2.5 mt-5">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium rounded bg-gray-500 text-white cursor-pointer hover:bg-gray-600 transition-colors"
          >
            {cancelLabel}
          </button>
          {onSubmit && (
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium rounded bg-blue-600 text-white cursor-pointer hover:bg-blue-700 transition-colors"
            >
              {submitLabel}
            </button>
          )}
        </div>
      )}
      {footer}
    </>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1000]">
      <div
        className={`bg-white p-5 rounded-lg shadow-lg ${width} w-full font-[Arial] max-h-[90vh] overflow-y-auto`}
      >
        <h2 className="text-lg font-semibold mb-4">{title}</h2>

        {onSubmit ? (
          <form onSubmit={handleSubmit}>{content}</form>
        ) : (
          content
        )}
      </div>
    </div>
  );
}
